import React, { useState, useEffect } from 'react';
import { compose } from 'ramda';
import { CircularProgress, Tooltip } from '@material-ui/core';
import Store from 'store/store';
import api from 'api';
import PastPerformanceReportChart from 'components/Charts/Report/PastPerformanceReportChart';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import TypicalAssetAllocationReportChart from 'components/Charts/Report/TypicalAssetAllocation';
import {
  mapRegionalData,
  mapSectorData
} from 'components/Charts/Utils/breakdowns';
import SectorBreakdownLegend from 'components/Breakdown/SectorBreakdownLegend';
import { getAllPerformanceData } from 'components/PastPerformance/utils';
import CountryBreakdown from 'components/Charts/Report/CountryBreakdown';

const { connect, selectors } = Store;
interface FactSheetLinkProps {
  investment: any;
  reportImages: any;
  imagesLoading: any;
  iconOnly?: boolean;
}

const FactSheetLink = ({
  investment,
  reportImages,
  imagesLoading = false,
  iconOnly = false
}: FactSheetLinkProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [fundPastPerformance, setFundPastPerformance] = useState({
    data: [],
    errors: [],
    investments: [] as any,
    investment: [],
    tabularData: [],
    earliestDates: {},
    footprints: {}
  });

  const [assetAllocation, setAssetAllocation] = useState([]);
  const [countryBreakdown, setCountryBreakdown] = useState([]);
  const [regionalBreakdown, setRegionalBreakdown] = useState(null);
  const [sectorBreakdown, setSectorBreakdown] = useState([]);
  const [renderChart, setRenderChart] = useState(false);

  const [chartIds] = useState({
    past_performance: `factsheet_performance_chart_${investment.id}`,
    asset_allocation: `asset_allocation_${investment.id}`,
    country_breakdown: `country_breakdown_${investment.id}`,
    regional_breakdown: `regional_breakdown_${investment.id}`,
    sector_breakdown: `sector_breakdown_${investment.id}`
  }) as any;

  const downloadFactsheet = () => {
    api
      .getFactsheet({
        id: investment.id,
        performanceData: getAllPerformanceData(fundPastPerformance.tabularData),
        ...Object.entries(chartIds).reduce((acc: any, [key, chartId]: any) => {
          acc[key] = reportImages[chartId];
          return acc;
        }, {})
      })
      .then(({ data }) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Factsheet.pdf');
        document.body.appendChild(link);
        link.click();
        setIsLoading(false);
        setRenderChart(false);
      })
      .catch(() => {
        setIsLoading(false);
        setRenderChart(false);
      });
  };

  useEffect(() => {
    const chartsReady = Object.values(chartIds).every(
      (id: string) =>
        !imagesLoading.includes(id) && id in reportImages && renderChart
    );

    if (chartsReady) {
      downloadFactsheet();
    }
  }, [
    imagesLoading.length,
    ...Object.values(chartIds).map((id: string) => reportImages[id])
  ]);

  const getFactsheet = () => {
    setIsLoading(true);
    setRenderChart(true);
    api.getFactsheetAssetAllocation(investment.id).then(({ data }) => {
      setAssetAllocation(data);
      if (!data.length) {
        delete chartIds.asset_allocation;
      }
    });
    api.getFactsheetSectorBreakdown(investment.id).then(({ data }) => {
      setSectorBreakdown(data);
      if (!data.length) {
        delete chartIds.sector_breakdown;
      }
    });
    api.getFactsheetCountryBreakdown(investment.id).then(({ data }) => {
      setCountryBreakdown(data);
      if (!data.length) {
        delete chartIds.country_breakdown;
      }
    });
    api.getFactsheetRegionalBreakdown(investment.id).then(({ data }) => {
      setRegionalBreakdown(data);
      if (!data.length) {
        delete chartIds.regional_breakdown;
      }
    });
    api.getFactsheetPastPerformance(investment.id).then(({ data }) => {
      setFundPastPerformance({
        data: data.data,
        errors: data.failed_codes,
        investments: [data.id],
        investment: investment,
        tabularData: data.tabular_data,
        earliestDates: data.earliest_dates,
        footprints: data.footprints
      });
    });
  };

  const mapAllocationData = (data: any) => {
    return data.map((allocation: any) => ({
      name: allocation.name,
      value: Number(allocation.value) * 100
    }));
  };

  return (
    <>
      {isLoading ? (
        <CircularProgress size="20px" />
      ) : !iconOnly ? (
        !investment.private && !investment.mp_id ? (
          <Tooltip title={'Download Fund Factsheet'} arrow>
            <span onClick={() => getFactsheet()} className="dashboardLink">
              {investment.name}
            </span>
          </Tooltip>
        ) : (
          investment.name
        )
      ) : (
        <DescriptionOutlinedIcon
          onClick={() => getFactsheet()}
          style={{ cursor: 'pointer', color: '#000' }}
        />
      )}

      {fundPastPerformance.data.length > 0 && renderChart && (
        <PastPerformanceReportChart
          data={fundPastPerformance.data}
          earliestDates={fundPastPerformance.earliestDates}
          footprints={fundPastPerformance.footprints}
          name={chartIds['past_performance']}
          showOverview={false}
          hideLegend={true}
          hideBenchmarks={true}
          height={300}
        />
      )}

      {assetAllocation.length > 0 && renderChart && (
        <TypicalAssetAllocationReportChart
          name={chartIds['asset_allocation']}
          data={mapAllocationData(assetAllocation)}
          isForFactSheet={true}
        />
      )}

      {countryBreakdown.length > 0 && renderChart && (
        <CountryBreakdown
          name={chartIds['country_breakdown']}
          data={countryBreakdown.map((allocation: any) => ({
            name: allocation.name,
            value: Number(allocation.value) * 100,
            equity: Number(allocation.equity) * 100,
            fixed_income: Number(allocation.fixed_income) * 100
          }))}
        />
      )}

      {regionalBreakdown !== null && renderChart && (
        <CountryBreakdown
          name={chartIds['regional_breakdown']}
          data={mapRegionalData(regionalBreakdown)}
        />
      )}

      {sectorBreakdown.length > 0 && renderChart && (
        <TypicalAssetAllocationReportChart
          name={chartIds['sector_breakdown']}
          data={mapAllocationData(mapSectorData(sectorBreakdown))}
          legend={SectorBreakdownLegend}
          isForReport={true}
          isForFactSheet={true}
        />
      )}
    </>
  );
};

export default compose(
  connect((state: any) => ({
    reportImages: selectors.reportSelectors.getImages(state),
    imagesLoading: selectors.reportSelectors.getImagesLoading(state)
  }))
)(FactSheetLink);
